import React from 'react';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import {
  Badge,
  Button,
  CertCatalogSelect,
  ConfirmationDialog,
  CountrySelect,
  CurrencySelect,
  DateInput,
  EmploymentStatusSelect,
  EngineTypeSelect,
  Label,
  ManningAgentSelect,
  NumberInput,
  PencilIcon,
  PlusIcon,
  QuestionnaireKindSelect,
  RankSelect,
  RelationSelect,
  Select,
  SignOffReasonSelect,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  TextInput,
  TrashIcon,
  UploadCloudIcon,
  VesselSelect,
  VesselTypeSelect,
  type LookupSelectProps,
} from '../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import type { CrewFormState } from './formMappers';
import {
  emptyAllotteeRow,
  emptyAppraisalRow,
  emptyCertRow,
  emptyFeedbackRow,
  emptyMedicalRow,
  emptyNokRow,
  emptyPrevServiceRow,
  emptyTransactionRow,
} from './crewRecordFactories';
import type { CrewDocumentRow, CrewLookups, LookupItem } from './types';

const lookupOptions = (items: LookupItem[] | undefined) =>
  (items ?? []).map((i) => ({
    value: String(i.id),
    label: i.abrv ? `${i.name} (${i.abrv})` : i.name,
  }));

const countryOptions = (items: CrewLookups['countries'] | undefined) =>
  (items ?? []).map((c) => ({
    value: String(c.id),
    label: c.isoCode2 ? `${c.name} (${c.isoCode2})` : c.name,
  }));

const vesselOptions = (items: CrewLookups['vessels'] | undefined) =>
  (items ?? []).map((v) => ({
    value: String(v.id),
    label: v.vesselName,
  }));

const APPRAISAL_TYPE_OPTIONS = [
  { value: 'APPRAISAL', label: 'Appraisal' },
  { value: 'DISCHARGE', label: 'Discharge' },
];

const CHOICE_OPTIONS = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
  { value: 'NA', label: 'N/A' },
];

const TRANSACTION_STATUS_OPTIONS = [
  { value: 'ONB', label: 'On board' },
  { value: 'OFB', label: 'Off board' },
];

function serviceTypeBadge(label: string) {
  return (
    <Badge
      variant={label === 'Company' ? 'info' : 'secondary'}
      className="shrink-0 rounded-md border px-2 py-0.5"
    >
      {label}
    </Badge>
  );
}

function serviceStatusBadge(status: string | null | undefined) {
  const code = status === 'ONB' || status === 'OFB' ? status : '';
  if (!code) return '-';
  return (
    <Badge
      variant={code === 'ONB' ? 'success' : 'default'}
      className="shrink-0 rounded-md border px-2 py-0.5"
    >
      {code}
    </Badge>
  );
}

const DEFAULT_ALLOTTEE_FREQUENCY_OPTIONS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ONCE', label: 'Once' },
];

const DEFAULT_MEDICAL_KIND_OPTIONS = [
  { value: 'FITNESS', label: 'Fitness' },
  { value: 'MEDICAL_EXAM', label: 'Medical exam' },
  { value: 'DRUG_ALCOHOL', label: 'Drug & alcohol' },
  { value: 'OTHER', label: 'Other' },
];

const DEFAULT_MEDICAL_RESULT_OPTIONS = [
  { value: 'FIT', label: 'Fit' },
  { value: 'UNFIT', label: 'Unfit' },
  { value: 'TEMPORARY_UNFIT', label: 'Temporary unfit' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'OTHER', label: 'Other' },
];

type SelectOption = { value: string; label: string };

const scaleSelectOptions = (min: number, max: number, step: number): SelectOption[] => {
  const opts: SelectOption[] = [];
  const safeStep = step >= 1 ? step : 1;
  for (let v = min; v <= max; v += safeStep) {
    opts.push({ value: String(v), label: String(v) });
    if (opts.length > 200) break;
  }
  return opts;
};

type ContractColumn = NonNullable<CrewLookups['contractColumns']>[number];

const enumOptions = (values: string[] | undefined, fallback: SelectOption[]): SelectOption[] => {
  if (!values?.length) return fallback;
  return values.map((v) => ({
    value: v,
    label: fallback.find((o) => o.value === v)?.label ?? v.replace(/_/g, ' '),
  }));
};

const currencyOptions = (items: CrewLookups['currencies'] | undefined): SelectOption[] =>
  (items ?? []).map((c) => ({
    value: c.code,
    label: c.symbol ? `${c.name} (${c.symbol})` : c.name,
  }));

const sortedContractColumns = (items: CrewLookups['contractColumns'] | undefined): ContractColumn[] =>
  [...(items ?? [])]
    .filter((c) => c.isActive !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder);

const computeTotalWage = (
  wageValues: Record<string, string> | undefined,
  columns: ContractColumn[],
): string => {
  let total = 0;
  for (const col of columns) {
    if (!col.includeInTotal) continue;
    const raw = Number(wageValues?.[String(col.id)] ?? '');
    if (!Number.isFinite(raw)) continue;
    if (col.columnType === 'DEDUCTION') total -= raw;
    else if (col.columnType === 'EARNING') total += raw;
  }
  return Number.isFinite(total) ? String(total) : '';
};

const optionLabel = (options: SelectOption[], value: string | undefined) => {
  const v = value?.trim() ?? '';
  if (!v) return '-';
  return options.find((o) => o.value === v)?.label ?? v;
};

const displayText = (value: string | null | undefined) => (value?.trim() ? value : '-');
const displayDate = (value: string | null | undefined) => (value?.trim() ? formatDateGlobal(value) : '-');

/** Highlight window for certificates that expire soon (edit list). */
const CERT_EXPIRING_WITHIN_DAYS = 60;

const daysUntilDate = (iso?: string | null): number | null => {
  if (!iso?.trim()) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
};

type CertExpiryTone = 'expired' | 'expiring';

const certExpiryTone = (expiredDate?: string | null): CertExpiryTone | null => {
  const days = daysUntilDate(expiredDate);
  if (days == null) return null;
  if (days < 0) return 'expired';
  if (days <= CERT_EXPIRING_WITHIN_DAYS) return 'expiring';
  return null;
};
const displayBool = (value: boolean | undefined) => (value ? 'Yes' : 'No');

const appraisalAnswerFilled = (ans: {
  answerTypeSnapshot: string;
  scaleValue?: string;
  choiceValue?: string;
  textValue?: string;
}) => {
  if (ans.answerTypeSnapshot === 'SCALE') return Boolean(ans.scaleValue?.trim());
  if (ans.answerTypeSnapshot === 'YES_NO_NA') return Boolean(ans.choiceValue?.trim());
  return Boolean(ans.textValue?.trim());
};
const truncateText = (value: string | null | undefined, max = 60) => {
  const v = value?.trim() ?? '';
  if (!v) return '-';
  return v.length > max ? `${v.slice(0, max)}…` : v;
};

const gridClass = 'grid md:grid-cols-2 lg:grid-cols-3 gap-4';

type CertFormRow = CrewFormState['certs'][number];

const MISSING_CERT_KEY_PREFIX = 'missing-req-';
const isMissingCertRow = (row: { key: string }) => row.key.startsWith(MISSING_CERT_KEY_PREFIX);

type RankRequirementRow = {
  id: number;
  rankId: number;
  vesselId?: number | null;
  certCatalogId: number;
  nationalityId: number;
  certCatalog?: { id: number; name: string; code?: string | null } | null;
};

type CertReqStatus = 'OK' | 'MISSING' | 'EXPIRED';

function todayIsoLocal(): string {
  return new Date().toISOString().slice(0, 10);
}

function certRequirementStatus(certCatalogId: number, certs: CertFormRow[]): CertReqStatus {
  const today = todayIsoLocal();
  const held = certs.filter((c) => c.certId && Number(c.certId) === certCatalogId);
  if (held.length === 0) return 'MISSING';
  if (held.some((c) => !c.expiredDate || c.expiredDate >= today)) return 'OK';
  return 'EXPIRED';
}
type AllotteeFormRow = CrewFormState['allottees'][number];
type NokFormRow = CrewFormState['nextOfKin'][number];
type AppraisalFormRow = CrewFormState['appraisals'][number];
type FeedbackFormRow = CrewFormState['feedback'][number];
type PrevServiceFormRow = CrewFormState['prevServices'][number];
type TransactionFormRow = CrewFormState['transactions'][number];
type MedicalFormRow = CrewFormState['medicals'][number];

export type RecordSection =
  | 'certs'
  | 'allottees'
  | 'nextOfKin'
  | 'appraisals'
  | 'feedback'
  | 'prevServices'
  | 'transactions'
  | 'medicals';

type EditorDraft =
  | { section: 'certs'; row: CertFormRow }
  | { section: 'allottees'; row: AllotteeFormRow }
  | { section: 'nextOfKin'; row: NokFormRow }
  | { section: 'appraisals'; row: AppraisalFormRow }
  | { section: 'feedback'; row: FeedbackFormRow }
  | { section: 'prevServices'; row: PrevServiceFormRow }
  | { section: 'transactions'; row: TransactionFormRow }
  | { section: 'medicals'; row: MedicalFormRow };

type EditorState =
  | { open: false }
  | { open: true; mode: 'add' | 'edit' | 'view'; draft: EditorDraft };

const SECTION_LABELS: Record<RecordSection, string> = {
  certs: 'document',
  allottees: 'allottee',
  nextOfKin: 'next of kin',
  appraisals: 'appraisal',
  feedback: 'feedback',
  prevServices: 'previous service',
  transactions: 'company service',
  medicals: 'medical record',
};

const SECTION_SUBTITLES: Record<RecordSection, string> = {
  certs: 'License or certificate details, validity, and attached scan',
  allottees: 'Beneficiary details, contact, and banking instructions',
  nextOfKin: 'Emergency contact person and how to reach them',
  appraisals: 'Evaluation questionnaire, appraisal details, and supporting document',
  feedback: 'Feedback date and notes',
  prevServices: 'Prior vessel employment history',
  transactions: 'Sign-on / sign-off contract and wage details',
  medicals: 'Fitness and medical exam details',
};

function prevServiceIncomplete(row: {
  vesselName?: string | null;
  rankId?: string | number | null;
  vesselTypeId?: string | number | null;
  signOnDate?: string | null;
  startDate?: string | null;
  signOffDate?: string | null;
  endDate?: string | null;
}): boolean {
  const vesselName = (row.vesselName ?? '').trim();
  const rankId = row.rankId == null || row.rankId === '' ? null : Number(row.rankId);
  const vesselTypeId =
    row.vesselTypeId == null || row.vesselTypeId === '' ? null : Number(row.vesselTypeId);
  const from = (row.signOnDate || row.startDate || '').trim();
  const to = (row.signOffDate || row.endDate || '').trim();
  return (
    !vesselName ||
    rankId == null ||
    !Number.isFinite(rankId) ||
    vesselTypeId == null ||
    !Number.isFinite(vesselTypeId) ||
    !from ||
    !to
  );
}

export interface CrewRecordsSectionsProps {
  form: CrewFormState;
  setForm: React.Dispatch<React.SetStateAction<CrewFormState>>;
  lookups: CrewLookups | undefined;
  readOnly?: boolean;
  /** When false, hide add actions even if not read-only. */
  allowAdd?: boolean;
  /** When false, hide delete actions even if not read-only. */
  allowDelete?: boolean;
  crewId?: number;
  documents?: CrewDocumentRow[];
  onDocumentsChange?: React.Dispatch<React.SetStateAction<CrewDocumentRow[]>>;
  /** When set, opens the add modal for that section (header Add button). */
  addRequest?: { section: RecordSection; nonce: number } | null;
  onAddRequestHandled?: () => void;
  /** Hide per-table Add buttons when Add lives in the section header. */
  hideInlineAddButtons?: boolean;
  /** Per-source write flags for the combined Services section. Office users omit this. */
  serviceAccess?: {
    showPrevious: boolean;
    showCompany: boolean;
    previous: { canModify: boolean; canAdd: boolean; canDelete: boolean };
    company: { canModify: boolean; canAdd: boolean; canDelete: boolean };
  };
}

const CERT_FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp';

type CrewAttachKind = 'CERT' | 'APPRAISAL' | 'CONTRACT';

const CrewRecordsSections: React.FC<CrewRecordsSectionsProps> = ({
  form,
  setForm,
  lookups,
  readOnly = false,
  allowAdd = true,
  allowDelete = true,
  crewId = 0,
  documents = [],
  onDocumentsChange,
  addRequest = null,
  onAddRequestHandled,
  hideInlineAddButtons = false,
  serviceAccess,
}) => {
  const { colors, theme } = useTheme();
  const canAdd = !readOnly && allowAdd;
  const canDelete = !readOnly && allowDelete;
  const writeFor = (section: RecordSection) => {
    if (readOnly) return { canModify: false, canAdd: false, canDelete: false };
    if (serviceAccess && section === 'prevServices') return serviceAccess.previous;
    if (serviceAccess && section === 'transactions') return serviceAccess.company;
    return { canModify: !readOnly, canAdd, canDelete };
  };
  const isDark = theme === 'dark';
  const [editor, setEditor] = React.useState<EditorState>({ open: false });
  const [wageImportErrors, setWageImportErrors] = React.useState<Record<string, string>>({});
  const [wageImportingKey, setWageImportingKey] = React.useState<string | null>(null);
  const [recordFileUploading, setRecordFileUploading] = React.useState(false);
  const [recordFileError, setRecordFileError] = React.useState('');
  const [recordDragActive, setRecordDragActive] = React.useState(false);
  const recordFileInputRef = React.useRef<HTMLInputElement>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState<{
    section: RecordSection;
    key: string;
    summary?: string;
  } | null>(null);
  const [serviceTab, setServiceTab] = React.useState<'all' | 'previous' | 'company'>('all');
  const [editorFieldError, setEditorFieldError] = React.useState('');

  const rankOpts = React.useMemo(() => lookupOptions(lookups?.ranks), [lookups?.ranks]);
  const relationOpts = React.useMemo(() => lookupOptions(lookups?.relations), [lookups?.relations]);
  const manningAgentOpts = React.useMemo(() => lookupOptions(lookups?.manningAgents), [lookups?.manningAgents]);
  const certCatalogOpts = React.useMemo(() => lookupOptions(lookups?.certCatalogs), [lookups?.certCatalogs]);
  const employmentStatusOpts = React.useMemo(
    () => lookupOptions(lookups?.employmentStatuses),
    [lookups?.employmentStatuses],
  );
  const vesselTypeOpts = React.useMemo(() => lookupOptions(lookups?.vesselTypes), [lookups?.vesselTypes]);
  const engineTypeOpts = React.useMemo(() => lookupOptions(lookups?.engineTypes), [lookups?.engineTypes]);
  const signOffReasonOpts = React.useMemo(() => lookupOptions(lookups?.signOffReasons), [lookups?.signOffReasons]);
  const questionnaireKindOpts = React.useMemo(
    () => lookupOptions(lookups?.questionnaireKinds),
    [lookups?.questionnaireKinds],
  );
  const countryOpts = React.useMemo(() => countryOptions(lookups?.countries), [lookups?.countries]);
  const vesselOpts = React.useMemo(() => vesselOptions(lookups?.vessels), [lookups?.vessels]);
  const showPreviousServices = !serviceAccess || serviceAccess.showPrevious;
  const showCompanyServices = !serviceAccess || serviceAccess.showCompany;
  const combinedServices = React.useMemo(() => {
    const rows: Array<{
      key: string;
      section: 'prevServices' | 'transactions';
      typeLabel: string;
      status: string;
      vessel: string;
      rank: string;
      signOnDate: string;
      signOffDate: string;
      source: PrevServiceFormRow | TransactionFormRow;
    }> = [];
    if (showPreviousServices) {
      for (const row of form.prevServices) {
        rows.push({
          key: `prev-${row.key}`,
          section: 'prevServices',
          typeLabel: 'Previous',
          status: 'OFB',
          vessel: displayText(row.vesselName),
          rank: optionLabel(rankOpts, row.rankId),
          signOnDate: row.signOnDate ?? '',
          signOffDate: row.signOffDate ?? '',
          source: row,
        });
      }
    }
    if (showCompanyServices) {
      for (const row of form.transactions) {
        rows.push({
          key: `company-${row.key}`,
          section: 'transactions',
          typeLabel: 'Company',
          status: row.status,
          vessel: row.vesselId ? optionLabel(vesselOpts, row.vesselId) : displayText(row.vesselName),
          rank: optionLabel(rankOpts, row.rankId),
          signOnDate: row.signOnDate ?? '',
          signOffDate: row.signOffDate ?? '',
          source: row,
        });
      }
    }
    rows.sort((a, b) => {
      const aDate = a.signOnDate.trim();
      const bDate = b.signOnDate.trim();
      if (aDate && bDate && aDate !== bDate) return bDate.localeCompare(aDate);
      if (aDate && !bDate) return -1;
      if (!aDate && bDate) return 1;
      return a.typeLabel.localeCompare(b.typeLabel);
    });
    return rows;
  }, [form.prevServices, form.transactions, rankOpts, showCompanyServices, showPreviousServices, vesselOpts]);
  const currencyOpts = React.useMemo(() => currencyOptions(lookups?.currencies), [lookups?.currencies]);
  const contractColumns = React.useMemo(() => sortedContractColumns(lookups?.contractColumns), [lookups?.contractColumns]);
  const allotteeFrequencyOpts = React.useMemo(
    () => enumOptions(lookups?.allotteeFrequencies, DEFAULT_ALLOTTEE_FREQUENCY_OPTIONS),
    [lookups?.allotteeFrequencies],
  );
  const medicalKindOpts = React.useMemo(
    () => enumOptions(lookups?.medicalKinds, DEFAULT_MEDICAL_KIND_OPTIONS),
    [lookups?.medicalKinds],
  );
  const medicalResultOpts = React.useMemo(
    () => enumOptions(lookups?.medicalResults, DEFAULT_MEDICAL_RESULT_OPTIONS),
    [lookups?.medicalResults],
  );

  const findCrewDocument = React.useCallback(
    (fileName: string | null | undefined, kind: CrewAttachKind) => {
      const name = fileName?.trim();
      if (!name) return null;
      return (
        documents.find((d) => d.originalFilename === name && (d.kind ?? '').toUpperCase() === kind) ??
        documents.find((d) => d.originalFilename === name) ??
        null
      );
    },
    [documents],
  );

  const uploadRecordFile = async (
    fileList: FileList | null,
    opts: {
      kind: CrewAttachKind;
      section: 'certs' | 'appraisals' | 'transactions';
      field: 'certFileName' | 'appraisalFileName' | 'fileName';
    },
  ) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!crewId) {
      setRecordFileError('Save the crew file first, then attach files.');
      return;
    }
    setRecordFileUploading(true);
    setRecordFileError('');
    try {
      const formData = new FormData();
      formData.append('files', file);
      const { data } = await api.post(`/crew/${crewId}/files?kind=${opts.kind}`, formData);
      const created = (Array.isArray(data) ? data : data ? [data] : []) as CrewDocumentRow[];
      if (created.length) {
        onDocumentsChange?.((prev) => [...prev, ...created]);
        const uploaded = created[0];
        setEditor((prev) => {
          if (!prev.open || prev.draft.section !== opts.section) return prev;
          return {
            ...prev,
            draft: {
              section: opts.section,
              row: { ...prev.draft.row, [opts.field]: uploaded.originalFilename },
            } as EditorDraft,
          };
        });
      }
      if (recordFileInputRef.current) recordFileInputRef.current.value = '';
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setRecordFileError(
        Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'File upload failed.',
      );
    } finally {
      setRecordFileUploading(false);
    }
  };

  const downloadRecordFile = async (fileName: string, kind: CrewAttachKind) => {
    const doc = findCrewDocument(fileName, kind);
    if (!crewId || !doc) {
      setRecordFileError('Uploaded file not found.');
      return;
    }
    try {
      const response = await api.get(`/crew/${crewId}/files/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalFilename || fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setRecordFileError('Download failed.');
    }
  };

  const fieldsReadOnly = readOnly || (editor.open && editor.mode === 'view');

  const applyForm = (updater: (state: CrewFormState) => CrewFormState | Record<string, unknown>) => {
    setForm((state) => updater(state) as CrewFormState);
  };

  const readOnlyTextClass = cn('text-sm block min-h-[1.25rem]', colors.text);

  const renderTextField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
    options?: { required?: boolean; placeholder?: string; multiline?: boolean; span?: boolean; rows?: number },
  ) => (
    <div className={cn('min-w-0', options?.span && 'md:col-span-2 lg:col-span-3')}>
      <Label htmlFor={fieldsReadOnly ? undefined : id} required={options?.required}>
        {label}
      </Label>
      {fieldsReadOnly ? (
        <span className={readOnlyTextClass}>{displayText(value)}</span>
      ) : options?.multiline ? (
        <Textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={options?.placeholder}
          rows={options?.rows ?? 3}
        />
      ) : (
        <TextInput
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={options?.placeholder}
        />
      )}
    </div>
  );

  const renderSelectField = (
    label: string,
    id: string,
    value: string,
    options: SelectOption[],
    onChange: (value: string) => void,
    fieldOptions?: { required?: boolean; placeholder?: string; span?: boolean },
  ) => (
    <div className={cn('min-w-0', fieldOptions?.span && 'md:col-span-2 lg:col-span-3')}>
      <Label htmlFor={fieldsReadOnly ? undefined : id} required={fieldOptions?.required}>
        {label}
      </Label>
      {fieldsReadOnly ? (
        <span className={readOnlyTextClass}>{optionLabel(options, value)}</span>
      ) : (
        <Select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={[{ value: '', label: fieldOptions?.placeholder ?? 'Select…' }, ...options]}
          placeholder={fieldOptions?.placeholder ?? 'Select…'}
        />
      )}
    </div>
  );

  const lookupOptsFor = (SelectComp: React.ComponentType<LookupSelectProps>): SelectOption[] => {
    if (SelectComp === RankSelect) return rankOpts;
    if (SelectComp === RelationSelect) return relationOpts;
    if (SelectComp === ManningAgentSelect) return manningAgentOpts;
    if (SelectComp === CertCatalogSelect) return certCatalogOpts;
    if (SelectComp === EmploymentStatusSelect) return employmentStatusOpts;
    if (SelectComp === VesselTypeSelect) return vesselTypeOpts;
    if (SelectComp === EngineTypeSelect) return engineTypeOpts;
    if (SelectComp === SignOffReasonSelect) return signOffReasonOpts;
    if (SelectComp === QuestionnaireKindSelect) return questionnaireKindOpts;
    if (SelectComp === CountrySelect) return countryOpts;
    if (SelectComp === VesselSelect) return vesselOpts;
    if (SelectComp === CurrencySelect) return currencyOpts;
    return [];
  };

  const renderLookupField = (
    label: string,
    id: string,
    value: string,
    SelectComp: React.ComponentType<LookupSelectProps>,
    onChange: (value: string) => void,
    fieldOptions?: { required?: boolean; placeholder?: string; span?: boolean },
  ) => (
    <div className={cn('min-w-0', fieldOptions?.span && 'md:col-span-2 lg:col-span-3')}>
      <Label htmlFor={fieldsReadOnly ? undefined : id} required={fieldOptions?.required}>
        {label}
      </Label>
      {fieldsReadOnly ? (
        <span className={readOnlyTextClass}>{optionLabel(lookupOptsFor(SelectComp), value)}</span>
      ) : (
        <SelectComp
          name={id}
          value={value}
          includeIds={[value]}
          onChange={(e) => onChange(String(e.target.value))}
          placeholder={fieldOptions?.placeholder ?? 'Select…'}
          showClearButton
        />
      )}
    </div>
  );

  const renderDateField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className="min-w-0">
      <Label htmlFor={fieldsReadOnly ? undefined : id}>{label}</Label>
      {fieldsReadOnly ? (
        <span className={readOnlyTextClass}>{displayDate(value)}</span>
      ) : (
        <DateInput id={id} value={value} onChange={(e) => onChange(e.target.value)} showFormatHint={false} />
      )}
    </div>
  );

  const renderNumberField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className="min-w-0">
      <Label htmlFor={fieldsReadOnly ? undefined : id}>{label}</Label>
      {fieldsReadOnly ? (
        <span className={readOnlyTextClass}>{displayText(value)}</span>
      ) : (
        <NumberInput
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value != null ? String(e.target.value) : '')}
        />
      )}
    </div>
  );

  const renderCheckboxField = (
    label: string,
    id: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
  ) => (
    <div className="min-w-0 flex items-end pb-1">
      {fieldsReadOnly ? (
        <div>
          <Label>{label}</Label>
          <span className={readOnlyTextClass}>{displayBool(checked)}</span>
        </div>
      ) : (
        <label htmlFor={id} className={cn('flex items-center gap-2 text-sm cursor-pointer', colors.text)}>
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded"
          />
          {label}
        </label>
      )}
    </div>
  );

  const renderInlineFieldGroup = (
    title: string,
    children: React.ReactNode,
    description?: string,
    layout: 'grid' | 'stack' = 'grid',
  ) => (
    <section
      className={cn(
        'overflow-hidden rounded-xl border shadow-sm',
        colors.border,
        colors.surface,
      )}
    >
      <div
        className={cn(
          'border-b px-4 py-3',
          colors.border,
          colors.headerBg,
        )}
      >
        <h3 className={cn('text-xs font-semibold uppercase tracking-wider', colors.text)}>{title}</h3>
        {description ? <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{description}</p> : null}
      </div>
      <div className={cn('p-4', layout === 'grid' ? gridClass : 'space-y-3')}>{children}</div>
    </section>
  );

  const removeRow = (key: RecordSection, rowKey: string) => {
    applyForm((s) => {
      switch (key) {
        case 'certs':
          return { ...s, certs: s.certs.filter((row) => row.key !== rowKey) };
        case 'allottees':
          return { ...s, allottees: s.allottees.filter((row) => row.key !== rowKey) };
        case 'nextOfKin':
          return { ...s, nextOfKin: s.nextOfKin.filter((row) => row.key !== rowKey) };
        case 'appraisals':
          return { ...s, appraisals: s.appraisals.filter((row) => row.key !== rowKey) };
        case 'feedback':
          return { ...s, feedback: s.feedback.filter((row) => row.key !== rowKey) };
        case 'prevServices':
          return { ...s, prevServices: s.prevServices.filter((row) => row.key !== rowKey) };
        case 'transactions':
          return { ...s, transactions: s.transactions.filter((row) => row.key !== rowKey) };
        case 'medicals':
          return { ...s, medicals: s.medicals.filter((row) => row.key !== rowKey) };
        default:
          return s;
      }
    });
  };

  const closeEditor = () => {
    setRecordFileError('');
    setEditorFieldError('');
    setRecordDragActive(false);
    setEditor({ open: false });
  };

  const openAdd = (section: RecordSection) => {
    if (!writeFor(section).canAdd) return;
    setRecordFileError('');
    setEditorFieldError('');
    const draft = (() => {
      switch (section) {
        case 'certs':
          return { section, row: emptyCertRow() } as EditorDraft;
        case 'allottees':
          return { section, row: emptyAllotteeRow() } as EditorDraft;
        case 'nextOfKin':
          return { section, row: emptyNokRow() } as EditorDraft;
        case 'appraisals':
          return { section, row: emptyAppraisalRow() } as EditorDraft;
        case 'feedback':
          return { section, row: emptyFeedbackRow() } as EditorDraft;
        case 'prevServices':
          return { section, row: emptyPrevServiceRow() } as EditorDraft;
        case 'transactions':
          return { section, row: emptyTransactionRow() } as EditorDraft;
        case 'medicals':
          return { section, row: emptyMedicalRow() } as EditorDraft;
      }
    })();
    setEditor({ open: true, mode: 'add', draft });
  };

  const openAddMissingCert = (certCatalogId: number) => {
    setRecordFileError('');
    setEditor({
      open: true,
      mode: 'add',
      draft: {
        section: 'certs',
        row: { ...emptyCertRow(), certId: String(certCatalogId) },
      },
    });
  };

  const activateRecordRow = (section: RecordSection, row: EditorDraft['row']) => {
    if (section === 'certs' && isMissingCertRow(row)) {
      if (readOnly) return;
      openAddMissingCert(Number((row as CertFormRow).certId));
      return;
    }
    openEdit(section, row);
  };

  React.useEffect(() => {
    if (!addRequest) return;
    openAdd(addRequest.section);
    onAddRequestHandled?.();
    // Intentionally only react to nonce changes from the header Add button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRequest?.nonce]);

  const openEdit = (section: RecordSection, row: EditorDraft['row']) => {
    setRecordFileError('');
    const cloned =
      section === 'transactions'
        ? {
            ...(row as TransactionFormRow),
            wageValues: { ...((row as TransactionFormRow).wageValues ?? {}) },
          }
        : { ...row };
    setEditor({
      open: true,
      mode: writeFor(section).canModify ? 'edit' : 'view',
      draft: { section, row: cloned } as EditorDraft,
    });
  };

  const patchEditorDraft = (patch: Record<string, unknown>) => {
    setEditor((prev) => {
      if (!prev.open) return prev;
      return {
        ...prev,
        draft: {
          ...prev.draft,
          row: { ...prev.draft.row, ...patch },
        } as EditorDraft,
      };
    });
  };

  const saveEditor = () => {
    if (!editor.open || editor.mode === 'view') return;
    const { mode, draft } = editor;
    const { section } = draft;
    if (section === 'prevServices') {
      const row = draft.row as PrevServiceFormRow;
      if (prevServiceIncomplete(row)) {
        setEditorFieldError(
          'Vessel name, rank, vessel type, a from date (sign on or start), and a to date (sign off or end) are required.',
        );
        return;
      }
    }
    setEditorFieldError('');
    applyForm((s) => {
      switch (section) {
        case 'certs': {
          const row = draft.row as CertFormRow;
          if (mode === 'add') return { ...s, certs: [...s.certs, row] };
          return { ...s, certs: s.certs.map((r) => (r.key === row.key ? row : r)) };
        }
        case 'allottees': {
          const row = draft.row as AllotteeFormRow;
          if (mode === 'add') return { ...s, allottees: [...s.allottees, row] };
          return { ...s, allottees: s.allottees.map((r) => (r.key === row.key ? row : r)) };
        }
        case 'nextOfKin': {
          const row = draft.row as NokFormRow;
          if (mode === 'add') return { ...s, nextOfKin: [...s.nextOfKin, row] };
          return { ...s, nextOfKin: s.nextOfKin.map((r) => (r.key === row.key ? row : r)) };
        }
        case 'appraisals': {
          const row = draft.row as AppraisalFormRow;
          if (mode === 'add') return { ...s, appraisals: [...s.appraisals, row] };
          return { ...s, appraisals: s.appraisals.map((r) => (r.key === row.key ? row : r)) };
        }
        case 'feedback': {
          const row = draft.row as FeedbackFormRow;
          if (mode === 'add') return { ...s, feedback: [...s.feedback, row] };
          return { ...s, feedback: s.feedback.map((r) => (r.key === row.key ? row : r)) };
        }
        case 'prevServices': {
          const row = draft.row as PrevServiceFormRow;
          if (mode === 'add') return { ...s, prevServices: [...s.prevServices, row] };
          return { ...s, prevServices: s.prevServices.map((r) => (r.key === row.key ? row : r)) };
        }
        case 'transactions': {
          const row = draft.row as TransactionFormRow;
          if (mode === 'add') return { ...s, transactions: [...s.transactions, row] };
          return { ...s, transactions: s.transactions.map((r) => (r.key === row.key ? row : r)) };
        }
        case 'medicals': {
          const row = draft.row as MedicalFormRow;
          if (mode === 'add') return { ...s, medicals: [...s.medicals, row] };
          return { ...s, medicals: s.medicals.map((r) => (r.key === row.key ? row : r)) };
        }
        default:
          return s;
      }
    });
    closeEditor();
  };

  const updateTransactionInForm = (rowKey: string, patch: Partial<TransactionFormRow>) => {
    applyForm((s) => ({
      ...s,
      transactions: s.transactions.map((t) => (t.key === rowKey ? { ...t, ...patch } : t)),
    }));
  };

  const patchTransactionDraft = (patch: Partial<TransactionFormRow>) => {
    setEditor((prev) => {
      if (!prev.open || prev.draft.section !== 'transactions') return prev;
      return {
        ...prev,
        draft: {
          section: 'transactions',
          row: { ...(prev.draft.row as TransactionFormRow), ...patch },
        },
      };
    });
  };

  const setTransactionWageValue = (rowKey: string, columnId: string, amount: string) => {
    const applyWage = (row: TransactionFormRow): TransactionFormRow => {
      const wageValues = { ...(row.wageValues ?? {}), [columnId]: amount };
      return { ...row, wageValues, totalWage: computeTotalWage(wageValues, contractColumns) };
    };

    if (editor.open && editor.draft.section === 'transactions' && editor.draft.row.key === rowKey) {
      patchTransactionDraft(applyWage(editor.draft.row as TransactionFormRow));
    } else {
      applyForm((s) => ({
        ...s,
        transactions: s.transactions.map((t) => (t.key === rowKey ? applyWage(t) : t)),
      }));
    }
  };

  const importWageScale = async (row: TransactionFormRow) => {
    if (!row.vesselId || !row.rankId || !form.nationalityId) {
      setWageImportErrors((prev) => ({
        ...prev,
        [row.key]: 'Select vessel and rank on this company service, and nationality on the Personal tab.',
      }));
      return;
    }
    setWageImportingKey(row.key);
    setWageImportErrors((prev) => {
      const next = { ...prev };
      delete next[row.key];
      return next;
    });
    try {
      const asOf = row.signOnDate || new Date().toISOString().slice(0, 10);
      const { data: scale } = await api.get('/crew-wage/scales/resolve', {
        params: {
          vesselId: row.vesselId,
          rankId: row.rankId,
          nationalityId: form.nationalityId,
          asOf,
        },
        skipGlobalErrorToast: true,
      });
      const wageValues: Record<string, string> = {};
      for (const wv of scale.values ?? []) {
        wageValues[String(wv.columnId)] = wv.amount != null ? String(wv.amount) : '';
      }
      const patch = {
        wageValues,
        wageCurrency: scale.currency ?? '',
        wageScaleId: scale.id != null ? String(scale.id) : '',
        totalWage: computeTotalWage(wageValues, contractColumns),
      };
      if (editor.open && editor.draft.section === 'transactions' && editor.draft.row.key === row.key) {
        patchTransactionDraft(patch);
      } else {
        updateTransactionInForm(row.key, patch);
      }
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { message?: string | string[] } } })?.response?.status;
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      const message =
        status === 404
          ? 'No wage scale found for this vessel, rank and nationality.'
          : Array.isArray(msg)
            ? msg.join(' ')
            : typeof msg === 'string'
              ? msg
              : 'Failed to import wage scale.';
      setWageImportErrors((prev) => ({ ...prev, [row.key]: message }));
    } finally {
      setWageImportingKey(null);
    }
  };

  const rowDeleteSummary = (section: RecordSection, row: EditorDraft['row']): string => {
    switch (section) {
      case 'certs': {
        const r = row as CertFormRow;
        return [optionLabel(certCatalogOpts, r.certId), r.certNumber].filter((v) => v && v !== '-').join(' · ');
      }
      case 'allottees':
        return (row as AllotteeFormRow).fullName?.trim() || '';
      case 'nextOfKin':
        return (row as NokFormRow).fullName?.trim() || '';
      case 'appraisals': {
        const r = row as AppraisalFormRow;
        const kindName =
          optionLabel(questionnaireKindOpts, r.kindId) ||
          optionLabel(APPRAISAL_TYPE_OPTIONS, r.appraisalType);
        return [kindName, displayDate(r.assessmentDate)].filter((v) => v && v !== '-').join(' · ');
      }
      case 'feedback':
        return displayDate((row as FeedbackFormRow).feedbackDate);
      case 'prevServices':
        return (row as PrevServiceFormRow).vesselName?.trim() || '';
      case 'transactions': {
        const r = row as TransactionFormRow;
        return [
          optionLabel(TRANSACTION_STATUS_OPTIONS, r.status),
          r.vesselId ? optionLabel(vesselOpts, r.vesselId) : r.vesselName,
        ]
          .filter((v) => v && v !== '-')
          .join(' · ');
      }
      case 'medicals': {
        const r = row as MedicalFormRow;
        return [optionLabel(medicalKindOpts, r.kind), displayDate(r.examDate)]
          .filter((v) => v && v !== '-')
          .join(' · ');
      }
      default:
        return '';
    }
  };

  const requestDeleteRow = (section: RecordSection, row: EditorDraft['row']) => {
    setDeleteConfirm({
      section,
      key: row.key,
      summary: rowDeleteSummary(section, row) || undefined,
    });
  };

  const confirmDeleteRow = () => {
    if (!deleteConfirm) return;
    removeRow(deleteConfirm.section, deleteConfirm.key);
    setDeleteConfirm(null);
  };

  const renderRowActions = (section: RecordSection, row: EditorDraft['row']) => {
    if (section === 'certs' && isMissingCertRow(row)) {
      if (readOnly) return null;
      const catalogId = Number((row as CertFormRow).certId);
      return (
        <div className="flex items-center justify-end gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            title="Add document"
            onClick={() => openAddMissingCert(catalogId)}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title={writeFor(section).canModify ? 'Edit' : 'View'}
          aria-label={writeFor(section).canModify ? 'Edit' : 'View'}
          className={cn(colors.primaryFg, colors.interactiveHover)}
          onClick={() => openEdit(section, row)}
        >
          <PencilIcon className="h-4 w-4" />
        </Button>
        {!writeFor(section).canDelete ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="Delete"
            aria-label="Delete"
            className={cn(
              isDark ? 'text-red-400 hover:bg-red-950/40' : 'text-red-600 hover:bg-red-50',
              colors.interactiveHover,
            )}
            onClick={() => requestDeleteRow(section, row)}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  };

  const certCatalogById = React.useMemo(() => {
    const map = new Map<number, { categoryId?: number | null; sortOrder?: number | null; name: string }>();
    for (const c of lookups?.certCatalogs ?? []) {
      map.set(c.id, c);
    }
    return map;
  }, [lookups?.certCatalogs]);

  const certCategoryById = React.useMemo(() => {
    const map = new Map<number, { id: number; name: string; sortOrder?: number | null }>();
    for (const c of lookups?.certCategories ?? []) {
      map.set(c.id, c);
    }
    return map;
  }, [lookups?.certCategories]);

  const rankIdForReqs = form.rankId?.trim() || '';
  const nationalityIdForReqs = form.nationalityId?.trim() || '';
  const onbVesselIdForReqs = React.useMemo(() => {
    const onb = form.transactions.find((t) => t.status === 'ONB' && t.vesselId);
    return onb?.vesselId?.trim() || '';
  }, [form.transactions]);

  const rankRequirementsUrl = React.useMemo(() => {
    if (!rankIdForReqs) return null;
    const params = new URLSearchParams({ rankId: rankIdForReqs });
    if (onbVesselIdForReqs) params.set('vesselId', onbVesselIdForReqs);
    return `/crew/rank-requirements?${params.toString()}`;
  }, [rankIdForReqs, onbVesselIdForReqs]);

  const { data: rankRequirements } = useSWR<RankRequirementRow[]>(rankRequirementsUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const requiredCertCatalogIds = React.useMemo(() => {
    if (!nationalityIdForReqs || !rankRequirements?.length) return [] as number[];
    const natId = Number(nationalityIdForReqs);
    const forNat = rankRequirements.filter((r) => r.nationalityId === natId);
    const byCert = new Map<number, RankRequirementRow>();
    for (const r of forNat) {
      const existing = byCert.get(r.certCatalogId);
      if (!existing || (existing.vesselId == null && r.vesselId != null)) {
        byCert.set(r.certCatalogId, r);
      }
    }
    return Array.from(byCert.keys());
  }, [rankRequirements, nationalityIdForReqs]);

  const certReqStatusByCatalogId = React.useMemo(() => {
    const map = new Map<number, CertReqStatus>();
    for (const catalogId of requiredCertCatalogIds) {
      map.set(catalogId, certRequirementStatus(catalogId, form.certs));
    }
    return map;
  }, [requiredCertCatalogIds, form.certs]);

  const certReqGapCount = React.useMemo(() => {
    let n = 0;
    certReqStatusByCatalogId.forEach((status) => {
      if (status !== 'OK') n += 1;
    });
    return n;
  }, [certReqStatusByCatalogId]);

  const certGroups = React.useMemo(() => {
    type Group = { key: string; label: string; sortOrder: number; rows: CertFormRow[] };
    const map = new Map<string, Group>();

    const pushRow = (row: CertFormRow) => {
      const catalogId = Number(row.certId);
      const catalog = Number.isFinite(catalogId) ? certCatalogById.get(catalogId) : undefined;
      const categoryId = catalog?.categoryId ?? null;
      const category = categoryId != null ? certCategoryById.get(categoryId) : undefined;
      const key = categoryId != null ? `cat-${categoryId}` : 'uncategorized';
      const label = category?.name?.trim() || 'Uncategorized';
      const sortOrder = category?.sortOrder ?? (categoryId == null ? 999999 : 0);
      const existing = map.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        map.set(key, { key, label, sortOrder, rows: [row] });
      }
    };

    for (const row of form.certs) {
      pushRow(row);
    }

    for (const catalogId of requiredCertCatalogIds) {
      if (certReqStatusByCatalogId.get(catalogId) !== 'MISSING') continue;
      const catalog = certCatalogById.get(catalogId);
      pushRow({
        ...emptyCertRow(),
        key: `${MISSING_CERT_KEY_PREFIX}${catalogId}`,
        certId: String(catalogId),
        notes: catalog?.name ? `Required: ${catalog.name}` : '',
      });
    }

    const groups = Array.from(map.values()).sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.label.localeCompare(b.label);
    });

    for (const group of groups) {
      group.rows.sort((a, b) => {
        const aMissing = isMissingCertRow(a) ? 0 : 1;
        const bMissing = isMissingCertRow(b) ? 0 : 1;
        if (aMissing !== bMissing) return aMissing - bMissing;
        const aName = optionLabel(certCatalogOpts, a.certId);
        const bName = optionLabel(certCatalogOpts, b.certId);
        return aName.localeCompare(bName);
      });
    }

    return groups;
  }, [
    form.certs,
    certCatalogById,
    certCategoryById,
    requiredCertCatalogIds,
    certReqStatusByCatalogId,
    certCatalogOpts,
  ]);

  const renderRecordsTable = ({
    columns,
    rows,
    emptyMessage,
    addLabel,
    section,
    renderCells,
    groups,
    getRowClassName,
    inlineAdd,
  }: {
    columns: string[];
    rows: EditorDraft['row'][];
    emptyMessage: string;
    addLabel: string;
    section: RecordSection;
    /** Cell values in column order (not `<td>` elements). */
    renderCells: (row: EditorDraft['row']) => React.ReactNode[];
    groups?: { key: string; label: string; rows: EditorDraft['row'][] }[];
    getRowClassName?: (row: EditorDraft['row']) => string | undefined;
    /** Show this table's Add button even when header Add is used elsewhere. */
    inlineAdd?: boolean;
  }) => {
    const useGroups = Array.isArray(groups) && groups.length > 0;
    const totalRows = useGroups ? groups.reduce((n, g) => n + g.rows.length, 0) : rows.length;

    const tableColumns: Column<EditorDraft['row'] & Record<string, unknown>>[] = columns.map(
      (header, index) => ({
        key: `c${index}`,
        header,
        sortable: false,
        render: (_v, row) => renderCells(row)[index],
      }),
    );

    const renderTable = (tableRows: EditorDraft['row'][]) => (
      <div className="min-h-0">
        <DataTable
          data={tableRows as Array<EditorDraft['row'] & Record<string, unknown>>}
          columns={tableColumns}
          keyExtractor={(row) => row.key}
          emptyMessage={emptyMessage}
          actions={(row) => renderRowActions(section, row)}
          density="compact"
          tableWrapperClassName="w-full"
          onRowClick={(row) => activateRecordRow(section, row)}
          getRowClassName={getRowClassName}
        />
      </div>
    );

    /** One shared table so category groups keep columns on the same vertical lines. */
    const renderGroupedTable = () => {
      const colCount = columns.length + 1; // + actions
      const colWidths = [
        '2.75rem', // actions
        '20%', // certificate
        '7rem', // status
        '11%',
        '11%',
        '10%',
        '12%',
        '12%',
        '7%',
      ];
      while (colWidths.length < colCount) colWidths.push('auto');

      return (
        <div className={cn('overflow-x-auto rounded-xl border', colors.border)}>
          <table className="w-full table-fixed border-collapse text-sm">
            <colgroup>
              {Array.from({ length: colCount }, (_, i) => (
                <col key={i} style={{ width: colWidths[i] ?? 'auto' }} />
              ))}
            </colgroup>
            <thead>
              <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.headerBg, colors.textMuted)}>
                <th className="px-2 py-2.5 font-medium" />
                {columns.map((header) => (
                  <th key={header} className="px-3 py-2.5 font-medium whitespace-nowrap">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups!.map((group) => (
                <React.Fragment key={group.key}>
                  <tr className={cn(colors.headerBg)}>
                    <td
                      colSpan={colCount}
                      className={cn('border-b px-3 py-2', colors.border)}
                    >
                      <div className="flex items-center gap-2">
                        <span className={cn('text-xs font-semibold uppercase tracking-wider', colors.text)}>
                          {group.label}
                        </span>
                        <span
                          className={cn(
                            'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold',
                            colors.surfaceHover,
                            colors.textMuted,
                          )}
                        >
                          {group.rows.length}
                        </span>
                      </div>
                    </td>
                  </tr>
                  {group.rows.map((row) => {
                    const cells = renderCells(row);
                    const rowClass = getRowClassName?.(row);
                    return (
                      <tr
                        key={row.key}
                        onClick={() => activateRecordRow(section, row)}
                        className={cn(
                          'cursor-pointer border-b transition last:border-b-0',
                          colors.border,
                          colors.interactiveHover,
                          rowClass,
                        )}
                      >
                        <td
                          className="px-1 py-1.5 align-middle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {renderRowActions(section, row)}
                        </td>
                        {columns.map((header, index) => (
                          <td
                            key={header}
                            className={cn(
                              'px-3 py-2 align-middle truncate',
                              colors.text,
                            )}
                          >
                            {cells[index]}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      );
    };

    return (
      <div>
        {(inlineAdd || !hideInlineAddButtons) && writeFor(section).canAdd && (
          <div className="mb-3 flex justify-end">
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => openAdd(section)}>
              <PlusIcon className="h-3.5 w-3.5" />
              {addLabel}
            </Button>
          </div>
        )}
        {totalRows === 0 ? (
          <p className={cn('text-sm', colors.textMuted)}>{emptyMessage}</p>
        ) : useGroups ? (
          renderGroupedTable()
        ) : (
          renderTable(rows)
        )}
      </div>
    );
  };


  const renderRecordFileField = (opts: {
    rowKey: string;
    label: string;
    hint: string;
    attachedLabel: string;
    fileName: string;
    kind: CrewAttachKind;
    section: 'certs' | 'appraisals' | 'transactions';
    field: 'certFileName' | 'appraisalFileName' | 'fileName';
  }) => {
    const linkedDoc = findCrewDocument(opts.fileName, opts.kind);
    const uploadOpts = { kind: opts.kind, section: opts.section, field: opts.field };

    return (
      <div className="min-w-0 md:col-span-2 lg:col-span-3 space-y-3">
        <div>
          <Label>{opts.label}</Label>
          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{opts.hint}</p>
        </div>
        {fieldsReadOnly ? (
          <div
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3',
              colors.border,
              colors.surface,
            )}
          >
            <span className={cn('text-sm font-medium truncate', colors.text)}>{displayText(opts.fileName)}</span>
            {opts.fileName && linkedDoc && crewId ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void downloadRecordFile(opts.fileName, opts.kind)}
              >
                Download
              </Button>
            ) : null}
          </div>
        ) : !crewId ? (
          <div
            className={cn(
              'flex min-h-[160px] flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center',
              colors.border,
            )}
          >
            <p className={cn('text-sm font-medium', colors.text)}>Save the crew file first</p>
            <p className={cn('mt-1 text-xs', colors.textMuted)}>Then you can upload a file here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              ref={recordFileInputRef}
              type="file"
              accept={CERT_FILE_ACCEPT}
              className="hidden"
              onChange={(e) => void uploadRecordFile(e.target.files, uploadOpts)}
            />
            {!opts.fileName ? (
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (recordFileUploading) return;
                  recordFileInputRef.current?.click();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!recordFileUploading) recordFileInputRef.current?.click();
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(false);
                  if (!recordFileUploading) void uploadRecordFile(e.dataTransfer.files, uploadOpts);
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border-2 border-dashed px-3 py-2.5 text-left transition sm:gap-4 sm:px-4',
                  colors.border,
                  recordFileUploading && 'pointer-events-none opacity-70',
                  recordDragActive
                    ? cn(colors.primaryBorderAll, colors.info, 'shadow-sm')
                    : colors.interactiveHover,
                )}
              >
                <div
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition',
                    recordDragActive
                      ? cn(colors.info, colors.primaryFg)
                      : cn(colors.surfaceHover, colors.textMuted),
                  )}
                >
                  <UploadCloudIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-semibold truncate', colors.text)}>
                    {recordFileUploading
                      ? 'Uploading…'
                      : recordDragActive
                        ? 'Release to attach'
                        : 'Drop your document here'}
                  </p>
                  <p className={cn('text-xs truncate', colors.textMuted)}>or click to browse from your computer</p>
                </div>
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs font-medium',
                    colors.border,
                    colors.surface,
                    colors.text,
                  )}
                >
                  Choose file
                </span>
              </div>
            ) : (
              <div
                className={cn(
                  'overflow-hidden rounded-xl border',
                  colors.border,
                  colors.surface,
                  recordDragActive && cn('ring-2', colors.primaryBorderAll.replace('border-', 'ring-')),
                )}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setRecordDragActive(false);
                  if (!recordFileUploading) void uploadRecordFile(e.dataTransfer.files, uploadOpts);
                }}
              >
                <div className={cn('flex flex-wrap items-center gap-2 px-3 py-2.5 sm:gap-3', colors.headerBg)}>
                  <div
                    className={cn(
                      cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border shadow-sm', colors.border, colors.surface),
                      colors.border,
                    )}
                  >
                    <UploadCloudIcon className={cn('h-4 w-4', colors.textSecondary)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-sm font-semibold', colors.text)} title={opts.fileName}>
                      {opts.fileName}
                    </p>
                    <p className={cn('text-xs', colors.textMuted)}>
                      {recordDragActive ? 'Drop to replace this file' : opts.attachedLabel}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full h-8"
                      disabled={recordFileUploading}
                      onClick={() => recordFileInputRef.current?.click()}
                    >
                      {recordFileUploading ? 'Uploading…' : 'Replace'}
                    </Button>
                    {linkedDoc ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full h-8"
                        onClick={() => void downloadRecordFile(opts.fileName, opts.kind)}
                      >
                        Download
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'rounded-full h-8',
                        colors.textSecondary,
                        isDark ? 'hover:text-red-400' : 'hover:text-red-600',
                      )}
                      onClick={() => {
                        setRecordFileError('');
                        patchEditorDraft({ [opts.field]: '' });
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {recordFileError ? <p className={cn('text-xs', isDark ? 'text-red-400' : 'text-red-600')}>{recordFileError}</p> : null}
      </div>
    );
  };

  const renderCertForm = (row: CertFormRow) => (
    <div className="space-y-4">
      {renderInlineFieldGroup(
        'Document details',
        <>
          {renderLookupField('Document', `cert-certId-${row.key}`, row.certId, CertCatalogSelect, (v) =>
            patchEditorDraft({ certId: v }),
          { required: true })}
          {renderLookupField('Issue country', `cert-issueCountryId-${row.key}`, row.issueCountryId, CountrySelect, (v) =>
            patchEditorDraft({ issueCountryId: v }),
          )}
          {renderTextField('Document number', `cert-certNumber-${row.key}`, row.certNumber ?? '', (v) =>
            patchEditorDraft({ certNumber: v }),
          )}
        </>,
        'Catalog entry, country, and document number',
      )}
      {renderInlineFieldGroup(
        'Validity & status',
        <>
          {renderDateField('Issued date', `cert-issuedDate-${row.key}`, row.issuedDate ?? '', (v) =>
            patchEditorDraft({ issuedDate: v }),
          )}
          {renderDateField('Expiry date', `cert-expiredDate-${row.key}`, row.expiredDate ?? '', (v) =>
            patchEditorDraft({ expiredDate: v }),
          )}
          <div
            className={cn(
              'min-w-0 flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5',
              colors.border,
              colors.headerBg,
            )}
          >
            {fieldsReadOnly ? (
              <div>
                <Label>On vessel</Label>
                <span className={readOnlyTextClass}>{displayBool(row.onVessel)}</span>
              </div>
            ) : (
              <Switch
                id={`cert-onVessel-${row.key}`}
                label="On vessel"
                checked={row.onVessel ?? false}
                onChange={(e) => patchEditorDraft({ onVessel: e.target.checked })}
              />
            )}
          </div>
        </>,
        'Issue / expiry dates and whether the original is on board',
      )}
      {renderInlineFieldGroup(
        'Files & notes',
        <>
          {renderRecordFileField({
            rowKey: row.key,
            label: 'Document file',
            hint: 'PDF, Office, or image · max 15 MB · stored as a crew document',
            attachedLabel: 'Attached document file',
            fileName: row.certFileName ?? '',
            kind: 'CERT',
            section: 'certs',
            field: 'certFileName',
          })}
          {renderTextField('Notes', `cert-notes-${row.key}`, row.notes ?? '', (v) => patchEditorDraft({ notes: v }), {
            multiline: true,
            span: true,
            rows: 2,
          })}
        </>,
        'Scan or photo of the document',
        'stack',
      )}
    </div>
  );

  const renderAllotteeForm = (row: AllotteeFormRow) => (
    <div className="space-y-4">
      {renderInlineFieldGroup(
        'Person',
        <>
          {renderTextField('Full name', `allottee-fullName-${row.key}`, row.fullName, (v) =>
            patchEditorDraft({ fullName: v }),
          { required: true })}
          {renderLookupField('Relation', `allottee-relationId-${row.key}`, row.relationId, RelationSelect, (v) =>
            patchEditorDraft({ relationId: v }),
          )}
        </>,
        'Beneficiary name and relation to the crew member',
      )}
      {renderInlineFieldGroup(
        'Contact',
        <>
          {renderTextField('Phone', `allottee-phone-${row.key}`, row.phone ?? '', (v) => patchEditorDraft({ phone: v }))}
          {renderTextField('Mobile', `allottee-mobile-${row.key}`, row.mobile ?? '', (v) => patchEditorDraft({ mobile: v }))}
          {renderTextField('Email', `allottee-email-${row.key}`, row.email ?? '', (v) => patchEditorDraft({ email: v }))}
          {renderTextField('Home address', `allottee-homeAddress-${row.key}`, row.homeAddress ?? '', (v) =>
            patchEditorDraft({ homeAddress: v }),
          { span: true })}
        </>,
        'Phone, email, and address',
      )}
      {renderInlineFieldGroup(
        'Banking & amount',
        <>
          {renderTextField('Bank', `allottee-bank-${row.key}`, row.bank ?? '', (v) => patchEditorDraft({ bank: v }))}
          {renderTextField('Bank account', `allottee-bankAccount-${row.key}`, row.bankAccount ?? '', (v) =>
            patchEditorDraft({ bankAccount: v }),
          )}
          {renderNumberField('Amount', `allottee-amount-${row.key}`, row.amount, (v) => patchEditorDraft({ amount: v }))}
          {currencyOpts.length > 0
            ? renderLookupField('Currency', `allottee-currency-${row.key}`, row.currency ?? '', CurrencySelect, (v) =>
                patchEditorDraft({ currency: v }),
              )
            : renderTextField('Currency', `allottee-currency-${row.key}`, row.currency ?? '', (v) =>
                patchEditorDraft({ currency: v }),
              )}
          {renderDateField('Start date', `allottee-startDate-${row.key}`, row.startDate ?? '', (v) =>
            patchEditorDraft({ startDate: v }),
          )}
          {renderDateField('End date', `allottee-endDate-${row.key}`, row.endDate ?? '', (v) =>
            patchEditorDraft({ endDate: v }),
          )}
          {renderSelectField('Frequency', `allottee-frequency-${row.key}`, row.frequency ?? '', allotteeFrequencyOpts, (v) =>
            patchEditorDraft({ frequency: v }),
          )}
        </>,
        'Payment instructions and schedule',
      )}
    </div>
  );

  const renderNokForm = (row: NokFormRow) => (
    <div className="space-y-4">
      {renderInlineFieldGroup(
        'Person',
        <>
          {renderTextField('Full name', `nok-fullName-${row.key}`, row.fullName, (v) => patchEditorDraft({ fullName: v }), {
            required: true,
          })}
          {renderLookupField('Relation', `nok-relationId-${row.key}`, row.relationId, RelationSelect, (v) =>
            patchEditorDraft({ relationId: v }),
          )}
        </>,
        'Emergency contact identity',
      )}
      {renderInlineFieldGroup(
        'Contact',
        <>
          {renderTextField('Phone', `nok-phone-${row.key}`, row.phone ?? '', (v) => patchEditorDraft({ phone: v }))}
          {renderTextField('Mobile', `nok-mobile-${row.key}`, row.mobile ?? '', (v) => patchEditorDraft({ mobile: v }))}
          {renderTextField('Email', `nok-email-${row.key}`, row.email ?? '', (v) => patchEditorDraft({ email: v }))}
          {renderTextField('Home address', `nok-homeAddress-${row.key}`, row.homeAddress ?? '', (v) =>
            patchEditorDraft({ homeAddress: v }),
          { span: true })}
          {renderTextField('Notes', `nok-notes-${row.key}`, row.notes ?? '', (v) => patchEditorDraft({ notes: v }), {
            multiline: true,
            span: true,
            rows: 2,
          })}
        </>,
        'How to reach this contact',
      )}
    </div>
  );

  const renderAppraisalForm = (row: AppraisalFormRow) => {
    const selectedKind = (lookups?.questionnaireKinds ?? []).find((k) => String(k.id) === row.kindId);
    const kindCode = (selectedKind?.code ?? row.appraisalType ?? '').toUpperCase();
    const isAppraisalKind = kindCode === 'APPRAISAL';
    const isDischargeKind = kindCode === 'DISCHARGE';
    const questionnaireOpts = (lookups?.questionnaires ?? [])
      .filter((q) => !row.kindId || String(q.kindId) === row.kindId)
      .map((q) => ({ value: String(q.id), label: q.name }));
    const selectedQuestionnaire = (lookups?.questionnaires ?? []).find((q) => String(q.id) === row.questionnaireId);

    const applyKind = (kindId: string) => {
      const kind = (lookups?.questionnaireKinds ?? []).find((k) => String(k.id) === kindId);
      const code = (kind?.code ?? '').toUpperCase();
      const legacyType = code === 'APPRAISAL' || code === 'DISCHARGE' ? code : '';
      patchEditorDraft({
        kindId,
        appraisalType: legacyType,
        questionnaireId: '',
        answers: [],
        questionnaireAvg: '',
        ...(code !== 'APPRAISAL' ? { toBePromoted: false, badHabits: '' } : {}),
        ...(code !== 'DISCHARGE' ? { dismissalReasonId: '', dismissalReasonText: '' } : {}),
      });
    };

    const applyQuestionnaire = (questionnaireId: string) => {
      const q = (lookups?.questionnaires ?? []).find((item) => String(item.id) === questionnaireId);
      const prevByQuestion = new Map((row.answers ?? []).map((a) => [a.questionId, a]));
      const answers = (q?.questions ?? []).map((question) => {
        const prev = prevByQuestion.get(String(question.id));
        return {
          key: prev?.key ?? `ans-${question.id}-${Date.now()}`,
          questionId: String(question.id),
          questionLabelSnapshot: question.label,
          answerTypeSnapshot: question.answerType,
          scaleMinSnapshot: question.scaleMin != null ? String(question.scaleMin) : '',
          scaleMaxSnapshot: question.scaleMax != null ? String(question.scaleMax) : '',
          scaleStepSnapshot: question.scaleStep != null ? String(question.scaleStep) : '',
          scaleValue: prev?.scaleValue ?? '',
          choiceValue: prev?.choiceValue ?? '',
          textValue: prev?.textValue ?? '',
          comment: prev?.comment ?? '',
        };
      });
      patchEditorDraft({ questionnaireId, answers });
    };

    const patchAnswer = (answerKey: string, patch: Partial<(typeof row.answers)[number]>) => {
      patchEditorDraft({
        answers: (row.answers ?? []).map((a) => (a.key === answerKey ? { ...a, ...patch } : a)),
      });
    };

    return (
      <div className="space-y-4">
        {renderInlineFieldGroup(
          'Form',
          <>
            {renderLookupField('Kind', `appraisal-kindId-${row.key}`, row.kindId, QuestionnaireKindSelect, applyKind)}
            {renderSelectField(
              'Questionnaire',
              `appraisal-questionnaireId-${row.key}`,
              row.questionnaireId,
              questionnaireOpts,
              applyQuestionnaire,
            )}
            {renderLookupField('Vessel', `appraisal-vesselId-${row.key}`, row.vesselId, VesselSelect, (v) =>
              patchEditorDraft({ vesselId: v }),
            )}
            {renderDateField('Assessment date', `appraisal-assessmentDate-${row.key}`, row.assessmentDate ?? '', (v) =>
              patchEditorDraft({ assessmentDate: v }),
            )}
            {renderTextField('Assessor name', `appraisal-assessorName-${row.key}`, row.assessorName ?? '', (v) =>
              patchEditorDraft({ assessorName: v }),
            )}
            {renderLookupField(
              'Employment status',
              `appraisal-employStatusId-${row.key}`,
              row.employStatusId,
              EmploymentStatusSelect,
              (v) => patchEditorDraft({ employStatusId: v }),
            )}
          </>,
          'Kind, questionnaire, vessel, and assessor',
        )}

        {isAppraisalKind &&
          renderInlineFieldGroup(
            'Appraisal extras',
            <>
              <div
                className={cn(
                  'min-w-0 flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5',
                  colors.border,
                  colors.headerBg,
                )}
              >
                {fieldsReadOnly ? (
                  <div>
                    <Label>To be promoted</Label>
                    <span className={readOnlyTextClass}>{displayBool(row.toBePromoted)}</span>
                  </div>
                ) : (
                  <Switch
                    id={`appraisal-toBePromoted-${row.key}`}
                    label="To be promoted"
                    checked={row.toBePromoted ?? false}
                    onChange={(e) => patchEditorDraft({ toBePromoted: e.target.checked })}
                  />
                )}
              </div>
              {renderTextField('Bad habits', `appraisal-badHabits-${row.key}`, row.badHabits ?? '', (v) =>
                patchEditorDraft({ badHabits: v }),
              { multiline: true, span: true })}
            </>,
            'Promotion recommendation and habits',
          )}

        {isDischargeKind &&
          renderInlineFieldGroup(
            'Discharge extras',
            <>
              {renderLookupField(
                'Dismissal reason',
                `appraisal-dismissalReasonId-${row.key}`,
                row.dismissalReasonId,
                SignOffReasonSelect,
                (v) => patchEditorDraft({ dismissalReasonId: v }),
              )}
              {renderTextField(
                'Dismissal reason text',
                `appraisal-dismissalReasonText-${row.key}`,
                row.dismissalReasonText ?? '',
                (v) => patchEditorDraft({ dismissalReasonText: v }),
              { span: true })}
            </>,
            'Dismissal details for discharge forms',
          )}

        {renderInlineFieldGroup(
          'Answers',
          <>
            {!selectedQuestionnaire ? (
              <p className={cn('text-sm', colors.textMuted)}>Select a questionnaire to answer its questions.</p>
            ) : (row.answers ?? []).length === 0 ? (
              <p className={cn('text-sm', colors.textMuted)}>This questionnaire has no active questions.</p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className={cn('text-xs', colors.textMuted)}>{selectedQuestionnaire.name}</p>
                  <p className={cn('text-xs font-medium tabular-nums', colors.textSecondary)}>
                    {(row.answers ?? []).filter(appraisalAnswerFilled).length} of {(row.answers ?? []).length} answered
                  </p>
                </div>
                <div className="space-y-2">
                  {(row.answers ?? []).map((ans, index) => {
                    const min = Number(ans.scaleMinSnapshot);
                    const max = Number(ans.scaleMaxSnapshot);
                    const step = Number(ans.scaleStepSnapshot || 1);
                    const scaleOpts =
                      ans.answerTypeSnapshot === 'SCALE' && Number.isFinite(min) && Number.isFinite(max)
                        ? scaleSelectOptions(min, max, Number.isFinite(step) ? step : 1)
                        : [];
                    const typeLabel =
                      ans.answerTypeSnapshot === 'SCALE'
                        ? 'Score'
                        : ans.answerTypeSnapshot === 'YES_NO_NA'
                          ? 'Yes / No'
                          : 'Text';
                    const filled = appraisalAnswerFilled(ans);
                    return (
                      <div
                        key={ans.key}
                        className={cn('rounded-xl border px-3 py-3', colors.border, filled ? colors.surface : colors.headerBg)}
                      >
                        <div className="flex items-start gap-3">
                          <span
                            className={cn(
                              'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                              filled ? cn(colors.primary, colors.primaryText) : cn(colors.surface, colors.textMuted, 'border', colors.border),
                            )}
                          >
                            {index + 1}
                          </span>
                          <div className="min-w-0 flex-1 space-y-2.5">
                            <div className="flex items-start justify-between gap-3">
                              <p className={cn('text-sm font-medium leading-snug', colors.text)}>{ans.questionLabelSnapshot}</p>
                              <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-[11px] font-medium', colors.headerBg, colors.textMuted)}>
                                {typeLabel}
                              </span>
                            </div>
                            {ans.answerTypeSnapshot === 'SCALE' && scaleOpts.length > 0 ? (
                              scaleOpts.length <= 11 ? (
                                <div className="flex flex-wrap gap-1.5" role="group" aria-label={ans.questionLabelSnapshot}>
                                  {scaleOpts.map((opt) => {
                                    const selected = ans.scaleValue === opt.value;
                                    return (
                                      <button
                                        key={opt.value}
                                        type="button"
                                        disabled={fieldsReadOnly}
                                        onClick={() =>
                                          patchAnswer(ans.key, { scaleValue: selected ? '' : String(opt.value) })
                                        }
                                        className={cn(
                                          'h-8 min-w-8 rounded-md border px-2.5 text-sm font-medium transition-colors',
                                          selected
                                            ? cn(colors.primary, colors.primaryText, 'border-transparent')
                                            : cn(colors.border, colors.surface, colors.text, colors.interactiveHover),
                                          fieldsReadOnly && 'cursor-default',
                                        )}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <Select
                                  id={`appraisal-ans-scale-${ans.key}`}
                                  value={ans.scaleValue}
                                  options={scaleOpts}
                                  placeholder="Score"
                                  disabled={fieldsReadOnly}
                                  readOnly={fieldsReadOnly}
                                  size="sm"
                                  onChange={(e) => patchAnswer(ans.key, { scaleValue: e.target.value })}
                                />
                              )
                            ) : null}
                            {ans.answerTypeSnapshot === 'YES_NO_NA' ? (
                              <div className="flex flex-wrap gap-1.5" role="group" aria-label={ans.questionLabelSnapshot}>
                                {CHOICE_OPTIONS.map((opt) => {
                                  const selected = ans.choiceValue === opt.value;
                                  return (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      disabled={fieldsReadOnly}
                                      onClick={() =>
                                        patchAnswer(ans.key, { choiceValue: selected ? '' : opt.value })
                                      }
                                      className={cn(
                                        'h-8 rounded-md border px-3 text-sm font-medium transition-colors',
                                        selected
                                          ? cn(colors.primary, colors.primaryText, 'border-transparent')
                                          : cn(colors.border, colors.surface, colors.text, colors.interactiveHover),
                                        fieldsReadOnly && 'cursor-default',
                                      )}
                                    >
                                      {opt.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}
                            {ans.answerTypeSnapshot === 'TEXT' ? (
                              fieldsReadOnly ? (
                                <p className={cn('text-sm', colors.text)}>{displayText(ans.textValue)}</p>
                              ) : (
                                <Textarea
                                  id={`appraisal-ans-text-${ans.key}`}
                                  value={ans.textValue ?? ''}
                                  rows={2}
                                  placeholder="Write the answer"
                                  onChange={(e) => patchAnswer(ans.key, { textValue: e.target.value })}
                                />
                              )
                            ) : null}
                            {fieldsReadOnly ? (
                              ans.comment?.trim() ? (
                                <p className={cn('text-xs', colors.textMuted)}>{ans.comment}</p>
                              ) : null
                            ) : (
                              <TextInput
                                id={`appraisal-ans-comment-${ans.key}`}
                                value={ans.comment ?? ''}
                                placeholder="Comment"
                                onChange={(e) => patchAnswer(ans.key, { comment: e.target.value })}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>,
          selectedQuestionnaire ? 'One question at a time, with score or choice beside the wording' : 'Answers for the selected questionnaire',
          'stack',
        )}

        {renderInlineFieldGroup(
          'Notes & attachment',
          <>
            {renderTextField('Notes', `appraisal-notes-${row.key}`, row.notes ?? '', (v) => patchEditorDraft({ notes: v }), {
              multiline: true,
              span: true,
              rows: 2,
            })}
            {renderRecordFileField({
              rowKey: row.key,
              label: 'Appraisal file',
              hint: 'PDF, Office, or image · max 15 MB · stored as crew document (APPRAISAL)',
              attachedLabel: 'Attached appraisal file',
              fileName: row.appraisalFileName ?? '',
              kind: 'APPRAISAL',
              section: 'appraisals',
              field: 'appraisalFileName',
            })}
          </>,
          'Optional notes and uploaded document',
          'stack',
        )}
      </div>
    );
  };

  const renderFeedbackForm = (row: FeedbackFormRow) => (
    <div className="space-y-4">
      {renderInlineFieldGroup(
        'Feedback',
        <>
          {renderDateField('Feedback date', `feedback-feedbackDate-${row.key}`, row.feedbackDate, (v) =>
            patchEditorDraft({ feedbackDate: v }),
          )}
          {renderTextField('Notes', `feedback-notes-${row.key}`, row.notes ?? '', (v) => patchEditorDraft({ notes: v }), {
            multiline: true,
            span: true,
            rows: 2,
          })}
        </>,
        'Date and comments',
      )}
    </div>
  );

  const renderPrevServiceForm = (row: PrevServiceFormRow) => (
    <div className="space-y-4">
      {renderInlineFieldGroup(
        'Vessel',
        <>
          {renderTextField('Vessel name', `prev-vesselName-${row.key}`, row.vesselName ?? '', (v) =>
            patchEditorDraft({ vesselName: v }),
          )}
          {renderLookupField('Rank', `prev-rankId-${row.key}`, row.rankId, RankSelect, (v) =>
            patchEditorDraft({ rankId: v }),
          )}
          {renderLookupField('Vessel type', `prev-vesselTypeId-${row.key}`, row.vesselTypeId, VesselTypeSelect, (v) =>
            patchEditorDraft({ vesselTypeId: v }),
          )}
          {renderLookupField('Vessel flag', `prev-vesselFlagId-${row.key}`, row.vesselFlagId, CountrySelect, (v) =>
            patchEditorDraft({ vesselFlagId: v }),
          )}
          {renderLookupField('Engine type', `prev-engTypeId-${row.key}`, row.engTypeId, EngineTypeSelect, (v) =>
            patchEditorDraft({ engTypeId: v }),
          )}
          {renderTextField('Engine type (text)', `prev-engType-${row.key}`, row.engType ?? '', (v) =>
            patchEditorDraft({ engType: v }),
          )}
        </>,
        'Ship, rank, and machinery',
      )}
      {renderInlineFieldGroup(
        'Tonnage',
        <>
          {renderNumberField('DWT', `prev-dwt-${row.key}`, row.dwt, (v) => patchEditorDraft({ dwt: v }))}
          {renderNumberField('GRT', `prev-grt-${row.key}`, row.grt, (v) => patchEditorDraft({ grt: v }))}
          {renderNumberField('NRT', `prev-nrt-${row.key}`, row.nrt, (v) => patchEditorDraft({ nrt: v }))}
        </>,
        'Deadweight and registered tonnage',
      )}
      {renderInlineFieldGroup(
        'Sign on',
        <>
          {renderDateField('Start date', `prev-startDate-${row.key}`, row.startDate ?? '', (v) =>
            patchEditorDraft({ startDate: v }),
          )}
          {renderDateField('Sign on date', `prev-signOnDate-${row.key}`, row.signOnDate ?? '', (v) =>
            patchEditorDraft({ signOnDate: v }),
          )}
          {renderTextField('Sign on port', `prev-signOnPort-${row.key}`, row.signOnPort ?? '', (v) =>
            patchEditorDraft({ signOnPort: v }),
          )}
        </>,
        'Joining dates and port',
      )}
      {renderInlineFieldGroup(
        'Sign off',
        <>
          {renderDateField('Sign off date', `prev-signOffDate-${row.key}`, row.signOffDate ?? '', (v) =>
            patchEditorDraft({ signOffDate: v }),
          )}
          {renderDateField('End date', `prev-endDate-${row.key}`, row.endDate ?? '', (v) =>
            patchEditorDraft({ endDate: v }),
          )}
          {renderTextField('Sign off port', `prev-signOffPort-${row.key}`, row.signOffPort ?? '', (v) =>
            patchEditorDraft({ signOffPort: v }),
          )}
          {renderLookupField(
            'Sign off reason',
            `prev-signOffReasonId-${row.key}`,
            row.signOffReasonId,
            SignOffReasonSelect,
            (v) => patchEditorDraft({ signOffReasonId: v }),
          )}
        </>,
        'Leaving dates, port, and reason',
      )}
      {renderInlineFieldGroup(
        'Company',
        <>
          {renderTextField('Manager name', `prev-managerName-${row.key}`, row.managerName ?? '', (v) =>
            patchEditorDraft({ managerName: v }),
          )}
          {renderTextField('Manning agency', `prev-manningAgency-${row.key}`, row.manningAgency ?? '', (v) =>
            patchEditorDraft({ manningAgency: v }),
          )}
        </>,
        'Manager and manning agency',
      )}
      {renderInlineFieldGroup(
        'Notes',
        renderTextField('Notes', `prev-notes-${row.key}`, row.notes ?? '', (v) => patchEditorDraft({ notes: v }), {
          multiline: true,
          span: true,
          rows: 2,
        }),
        'Anything else about this service',
        'stack',
      )}
    </div>
  );

  const renderTransactionForm = (row: TransactionFormRow) => (
    <div className="space-y-4">
      {renderInlineFieldGroup(
        'Assignment',
        <>
          {renderSelectField('Status', `txn-status-${row.key}`, row.status, TRANSACTION_STATUS_OPTIONS, (v) =>
            patchEditorDraft({ status: v }),
          )}
          {renderLookupField('Vessel', `txn-vesselId-${row.key}`, row.vesselId, VesselSelect, (v) =>
            patchEditorDraft({ vesselId: v }),
          )}
          {renderTextField('Vessel name', `txn-vesselName-${row.key}`, row.vesselName ?? '', (v) =>
            patchEditorDraft({ vesselName: v }),
          )}
          {renderLookupField('Rank', `txn-rankId-${row.key}`, row.rankId, RankSelect, (v) =>
            patchEditorDraft({ rankId: v }),
          )}
          {renderLookupField(
            'Manning agency',
            `txn-manningAgencyId-${row.key}`,
            row.manningAgencyId,
            ManningAgentSelect,
            (v) => patchEditorDraft({ manningAgencyId: v }),
          )}
          {renderCheckboxField(
            'Flag state watchkeeper',
            `txn-flWatchkeeper-${row.key}`,
            row.flWatchkeeper ?? false,
            (v) => patchEditorDraft({ flWatchkeeper: v }),
          )}
        </>,
        'Status, vessel, rank, and agency',
      )}
      {renderInlineFieldGroup(
        'Sign on',
        <>
          {renderDateField('Start date', `txn-startDate-${row.key}`, row.startDate ?? '', (v) =>
            patchEditorDraft({ startDate: v }),
          )}
          {renderDateField('Sign on date', `txn-signOnDate-${row.key}`, row.signOnDate ?? '', (v) =>
            patchEditorDraft({ signOnDate: v }),
          )}
          {renderTextField('Sign on port', `txn-signOnPort-${row.key}`, row.signOnPort ?? '', (v) =>
            patchEditorDraft({ signOnPort: v }),
          )}
        </>,
        'Joining dates and port',
      )}
      {renderInlineFieldGroup(
        'Sign off',
        <>
          {renderDateField('Sign off date', `txn-signOffDate-${row.key}`, row.signOffDate ?? '', (v) =>
            patchEditorDraft({ signOffDate: v }),
          )}
          {renderDateField('End date', `txn-endDate-${row.key}`, row.endDate ?? '', (v) =>
            patchEditorDraft({ endDate: v }),
          )}
          {renderTextField('Sign off port', `txn-signOffPort-${row.key}`, row.signOffPort ?? '', (v) =>
            patchEditorDraft({ signOffPort: v }),
          )}
          {renderLookupField(
            'Sign off reason',
            `txn-signOffReasonId-${row.key}`,
            row.signOffReasonId,
            SignOffReasonSelect,
            (v) => patchEditorDraft({ signOffReasonId: v }),
          )}
          {renderDateField(
            'Intention sign off date',
            `txn-intentionSignOffDate-${row.key}`,
            row.intentionSignOffDate ?? '',
            (v) => patchEditorDraft({ intentionSignOffDate: v }),
          )}
        </>,
        'Leaving dates, port, and reason',
      )}
      {renderInlineFieldGroup(
        'Contract end',
        <>
          {renderDateField('COE date', `txn-coeDate-${row.key}`, row.coeDate ?? '', (v) =>
            patchEditorDraft({ coeDate: v }),
          )}
          {renderDateField('Extended COE date', `txn-extCoeDate-${row.key}`, row.extCoeDate ?? '', (v) =>
            patchEditorDraft({ extCoeDate: v }),
          )}
          {renderDateField('Prior COE date', `txn-priorCoeDate-${row.key}`, row.priorCoeDate ?? '', (v) =>
            patchEditorDraft({ priorCoeDate: v }),
          )}
          {renderDateField('Latest COE date', `txn-latestCoeDate-${row.key}`, row.latestCoeDate ?? '', (v) =>
            patchEditorDraft({ latestCoeDate: v }),
          )}
        </>,
        'Contract end of employment dates',
      )}
      {renderInlineFieldGroup(
        'Wages',
        <>
          {!fieldsReadOnly && (
            <div className="min-w-0 md:col-span-2 lg:col-span-3 flex flex-col items-start gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={wageImportingKey === row.key}
                onClick={() => void importWageScale(row)}
              >
                {wageImportingKey === row.key ? 'Importing…' : 'Import wage scale'}
              </Button>
              {wageImportErrors[row.key] && (
                <p className={cn('text-xs', isDark ? 'text-red-400' : 'text-red-600')}>{wageImportErrors[row.key]}</p>
              )}
            </div>
          )}
          {currencyOpts.length > 0
            ? renderSelectField(
                'Wage currency',
                `txn-wageCurrency-${row.key}`,
                row.wageCurrency ?? '',
                currencyOpts,
                (v) => patchEditorDraft({ wageCurrency: v }),
              )
            : renderTextField('Wage currency', `txn-wageCurrency-${row.key}`, row.wageCurrency ?? '', (v) =>
                patchEditorDraft({ wageCurrency: v }),
              )}
          {contractColumns.map((column) =>
            renderNumberField(
              column.name,
              `txn-wageCol-${column.id}-${row.key}`,
              row.wageValues?.[String(column.id)] ?? '',
              (v) => setTransactionWageValue(row.key, String(column.id), v),
            ),
          )}
          {renderNumberField('Included OT', `txn-includedOt-${row.key}`, row.includedOt, (v) =>
            patchEditorDraft({ includedOt: v }),
          )}
          <div className="min-w-0">
            <Label>Total wage</Label>
            <span className={readOnlyTextClass}>{displayText(row.totalWage)}</span>
          </div>
        </>,
        'Currency, wage columns, and total',
      )}
      {renderInlineFieldGroup(
        'Settlement',
        <>
          {renderNumberField('Accrued leave days', `txn-accruedLeaveDays-${row.key}`, row.accruedLeaveDays ?? '', (v) =>
            patchEditorDraft({ accruedLeaveDays: v }),
          )}
          {renderNumberField('Leave pay due', `txn-leavePayDue-${row.key}`, row.leavePayDue ?? '', (v) =>
            patchEditorDraft({ leavePayDue: v }),
          )}
          {renderNumberField('Final cash due', `txn-finalCashDue-${row.key}`, row.finalCashDue ?? '', (v) =>
            patchEditorDraft({ finalCashDue: v }),
          )}
          {renderTextField('Repatriation', `txn-repatriation-${row.key}`, row.repatriation ?? '', (v) =>
            patchEditorDraft({ repatriation: v }),
          )}
        </>,
        'Leave, cash due, and repatriation',
      )}
      {renderInlineFieldGroup(
        'File',
        renderRecordFileField({
          rowKey: row.key,
          label: 'Company service file',
          hint: 'PDF, Office, or image · max 15 MB · stored as a crew document',
          attachedLabel: 'Attached company service file',
          fileName: row.fileName ?? '',
          kind: 'CONTRACT',
          section: 'transactions',
          field: 'fileName',
        }),
        'Upload the contract or supporting document',
        'stack',
      )}
      {renderInlineFieldGroup(
        'Notes',
        renderTextField('Notes', `txn-notes-${row.key}`, row.notes ?? '', (v) => patchEditorDraft({ notes: v }), {
          multiline: true,
          span: true,
          rows: 2,
        }),
        'Comments about this company service',
        'stack',
      )}
    </div>
  );

  const renderMedicalForm = (row: MedicalFormRow) => (
    <div className="space-y-4">
      {renderInlineFieldGroup(
        'Exam',
        <>
          {renderSelectField('Kind', `medical-kind-${row.key}`, row.kind, medicalKindOpts, (v) =>
            patchEditorDraft({ kind: v }),
          )}
          {renderDateField('Exam date', `medical-examDate-${row.key}`, row.examDate ?? '', (v) =>
            patchEditorDraft({ examDate: v }),
          )}
          {renderDateField('Next due date', `medical-nextDueDate-${row.key}`, row.nextDueDate ?? '', (v) =>
            patchEditorDraft({ nextDueDate: v }),
          )}
          {renderTextField('Clinic name', `medical-clinicName-${row.key}`, row.clinicName ?? '', (v) =>
            patchEditorDraft({ clinicName: v }),
          )}
          {renderTextField('Certificate no.', `medical-certificateNo-${row.key}`, row.certificateNo ?? '', (v) =>
            patchEditorDraft({ certificateNo: v }),
          )}
        </>,
        'Kind of exam, dates, and clinic',
      )}
      {renderInlineFieldGroup(
        'Result',
        <>
          {renderSelectField('Result', `medical-result-${row.key}`, row.result, medicalResultOpts, (v) =>
            patchEditorDraft({ result: v }),
          )}
          {renderTextField(
            'Result notes',
            `medical-resultNotes-${row.key}`,
            row.resultNotes ?? '',
            (v) => patchEditorDraft({ resultNotes: v }),
            { span: true },
          )}
        </>,
        'Fitness outcome',
      )}
      {renderInlineFieldGroup(
        'Notes',
        <>
          {renderTextField('Notes', `medical-notes-${row.key}`, row.notes ?? '', (v) => patchEditorDraft({ notes: v }), {
            multiline: true,
            span: true,
            rows: 2,
          })}
        </>,
        'Extra comments',
        'stack',
      )}
    </div>
  );

  const renderEditorBody = () => {
    if (!editor.open) return null;
    const { draft } = editor;
    switch (draft.section) {
      case 'certs':
        return renderCertForm(draft.row);
      case 'allottees':
        return renderAllotteeForm(draft.row);
      case 'nextOfKin':
        return renderNokForm(draft.row);
      case 'appraisals':
        return renderAppraisalForm(draft.row);
      case 'feedback':
        return renderFeedbackForm(draft.row);
      case 'prevServices':
        return renderPrevServiceForm(draft.row);
      case 'transactions':
        return renderTransactionForm(draft.row);
      case 'medicals':
        return renderMedicalForm(draft.row);
      default:
        return null;
    }
  };

  const editorTitle = () => {
    if (!editor.open) return '';
    const label = SECTION_LABELS[editor.draft.section];
    if (editor.mode === 'add') return `Add ${label}`;
    if (editor.mode === 'view') return `View ${label}`;
    return `Edit ${label}`;
  };

  const editorSubtitle = () => {
    if (!editor.open) return '';
    return SECTION_SUBTITLES[editor.draft.section];
  };

  const modalSize = editor.open && editor.draft.section === 'transactions' ? '3xl' : '2xl';

  return (
    <>
      <TabsContent value="certs" className="mt-0">
        {!rankIdForReqs || !nationalityIdForReqs ? (
          <p className={cn('mb-3 text-xs', colors.textMuted)}>
            Set Rank and Nationality on Personal details to show required certificates in this table.
          </p>
        ) : certReqGapCount > 0 ? (
          <p className={cn('mb-3 text-xs', colors.textMuted)}>
            <span className={isDark ? 'text-red-300 font-medium' : 'text-red-700 font-medium'}>
              {certReqGapCount} gap{certReqGapCount === 1 ? '' : 's'}
            </span>
            {' '}vs rank requirements for this nationality
            {onbVesselIdForReqs ? ' (including vessel rules)' : ''}. Missing rows are listed with an Add action.
          </p>
        ) : requiredCertCatalogIds.length > 0 ? (
          <p className={cn('mb-3 text-xs', colors.textMuted)}>
            All required certificates for this rank and nationality are held and valid.
          </p>
        ) : null}
        {renderRecordsTable({
          section: 'certs',
          columns: ['Document', 'Status', 'Issue country', 'Number', 'Issued', 'Expiry', 'File', 'On vessel'],
          rows: form.certs,
          groups: certGroups,
          emptyMessage: 'No documents recorded.',
          addLabel: 'Add document',
          getRowClassName: (row) => {
            const r = row as CertFormRow;
            if (isMissingCertRow(r)) {
              return isDark ? 'bg-red-950/25 text-red-300' : 'bg-red-50/80 text-red-800';
            }
            const reqStatus = r.certId ? certReqStatusByCatalogId.get(Number(r.certId)) : undefined;
            if (reqStatus === 'EXPIRED') {
              return isDark ? 'bg-orange-950/30 text-orange-300' : 'bg-orange-50 text-orange-700';
            }
            const tone = certExpiryTone(r.expiredDate);
            if (tone === 'expired') {
              return isDark ? 'bg-red-950/35 text-red-300' : 'bg-red-50 text-red-700';
            }
            if (tone === 'expiring') {
              return isDark ? 'bg-orange-950/30 text-orange-300' : 'bg-orange-50 text-orange-700';
            }
            return undefined;
          },
          renderCells: (row) => {
            const r = row as CertFormRow;
            const missing = isMissingCertRow(r);
            const reqStatus = r.certId ? certReqStatusByCatalogId.get(Number(r.certId)) : undefined;
            const tone = missing ? 'expired' : certExpiryTone(r.expiredDate);
            const days = daysUntilDate(r.expiredDate);
            const toneTextClass =
              tone === 'expired'
                ? isDark
                  ? 'text-red-300 font-semibold'
                  : 'text-red-700 font-semibold'
                : tone === 'expiring'
                  ? isDark
                    ? 'text-orange-300 font-semibold'
                    : 'text-orange-700 font-semibold'
                  : undefined;
            const expiryLabel = missing
              ? '—'
              : tone === 'expired'
                ? `${displayDate(r.expiredDate)} · Expired`
                : tone === 'expiring' && days != null
                  ? `${displayDate(r.expiredDate)} · ${days}d`
                  : displayDate(r.expiredDate);
            const statusBadge =
              reqStatus == null ? (
                <span className={colors.textMuted}>—</span>
              ) : (
                <Badge
                  variant={reqStatus === 'OK' ? 'success' : reqStatus === 'EXPIRED' ? 'warning' : 'danger'}
                  className="rounded-md px-2 py-0.5"
                >
                  {reqStatus}
                </Badge>
              );
            return [
              <span key="name" className={cn(toneTextClass)}>
                {optionLabel(certCatalogOpts, r.certId)}
              </span>,
              statusBadge,
              missing ? '—' : optionLabel(countryOpts, r.issueCountryId),
              missing ? '—' : displayText(r.certNumber),
              missing ? '—' : displayDate(r.issuedDate),
              <span key="expiry" className={cn(toneTextClass)}>
                {expiryLabel}
              </span>,
              missing ? '—' : displayText(r.certFileName),
              missing ? '—' : displayBool(r.onVessel),
            ];
          },
        })}
      </TabsContent>

      <TabsContent value="allottees" className="mt-0">
        <p className={cn('text-xs mb-3', colors.textMuted)}>
          Allottee amount is the beneficiary instruction; contract allotment is set on the Company Services wage columns.
        </p>
        {renderRecordsTable({
          section: 'allottees',
          columns: ['Name', 'Relation', 'Amount', 'Currency', 'Frequency', 'Phone'],
          rows: form.allottees,
          emptyMessage: 'No allottees recorded.',
          addLabel: 'Add allottee',
          renderCells: (row) => {
            const r = row as AllotteeFormRow;
            return [
              displayText(r.fullName),
              optionLabel(relationOpts, r.relationId),
              displayText(r.amount),
              displayText(r.currency),
              optionLabel(allotteeFrequencyOpts, r.frequency),
              displayText(r.phone),
            ];
          },
        })}
      </TabsContent>

      <TabsContent value="nok" className="mt-0">
        {renderRecordsTable({
          section: 'nextOfKin',
          columns: ['Name', 'Relation', 'Phone', 'Mobile', 'Email'],
          rows: form.nextOfKin,
          emptyMessage: 'No next of kin recorded.',
          addLabel: 'Add next of kin',
          renderCells: (row) => {
            const r = row as NokFormRow;
            return [
              displayText(r.fullName),
              optionLabel(relationOpts, r.relationId),
              displayText(r.phone),
              displayText(r.mobile),
              displayText(r.email),
            ];
          },
        })}
      </TabsContent>

      <TabsContent value="appraisals" className="mt-0">
        {renderRecordsTable({
          section: 'appraisals',
          columns: ['Kind', 'Questionnaire', 'Vessel', 'Date', 'Assessor', 'Avg', 'File'],
          rows: form.appraisals,
          emptyMessage: 'No appraisals recorded.',
          addLabel: 'Add appraisal',
          renderCells: (row) => {
            const r = row as AppraisalFormRow;
            const qName =
              (lookups?.questionnaires ?? []).find((q) => String(q.id) === r.questionnaireId)?.name ?? '';
            return [
              optionLabel(questionnaireKindOpts, r.kindId) ||
                optionLabel(APPRAISAL_TYPE_OPTIONS, r.appraisalType),
              displayText(qName),
              optionLabel(vesselOpts, r.vesselId),
              displayDate(r.assessmentDate),
              displayText(r.assessorName),
              displayText(r.questionnaireAvg),
              displayText(r.appraisalFileName),
            ];
          },
        })}
      </TabsContent>

      <TabsContent value="feedback" className="mt-0">
        {renderRecordsTable({
          section: 'feedback',
          columns: ['Date', 'Notes'],
          rows: form.feedback,
          emptyMessage: 'No feedback recorded.',
          addLabel: 'Add feedback',
          renderCells: (row) => {
            const r = row as FeedbackFormRow;
            return [displayDate(r.feedbackDate), truncateText(r.notes)];
          },
        })}
      </TabsContent>

      <TabsContent value="services" className="!mt-0">
        <Tabs value={serviceTab} onValueChange={(value) => setServiceTab(value as 'all' | 'previous' | 'company')}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <TabsList variant="pill" fit>
              <TabsTrigger value="all" appearance="pill" size="sm" count={combinedServices.length}>
                All
              </TabsTrigger>
              {showPreviousServices && (
                <TabsTrigger value="previous" appearance="pill" size="sm" count={form.prevServices.length}>
                  Previous service
                </TabsTrigger>
              )}
              {showCompanyServices && (
                <TabsTrigger value="company" appearance="pill" size="sm" count={form.transactions.length}>
                  Company Services
                </TabsTrigger>
              )}
            </TabsList>
            <div className="flex flex-wrap justify-end gap-2">
              {showPreviousServices && serviceTab !== 'company' && writeFor('prevServices').canAdd && (
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => openAdd('prevServices')}>
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add previous service
                </Button>
              )}
              {showCompanyServices && serviceTab !== 'previous' && writeFor('transactions').canAdd && (
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => openAdd('transactions')}>
                  <PlusIcon className="h-3.5 w-3.5" />
                  Add company service
                </Button>
              )}
            </div>
          </div>

          <TabsContent value="all" className="!mt-0">
            <DataTable
              data={combinedServices}
              columns={[
                {
                  key: 'typeLabel',
                  header: 'Type',
                  sortable: false,
                  render: (_value, row) => serviceTypeBadge(row.typeLabel),
                },
                {
                  key: 'status',
                  header: 'Status',
                  sortable: false,
                  render: (_value, row) => serviceStatusBadge(row.status),
                },
                { key: 'vessel', header: 'Vessel', sortable: false },
                { key: 'rank', header: 'Rank', sortable: false },
                {
                  key: 'signOnDate',
                  header: 'Sign on',
                  sortable: false,
                  render: (_value, row) => displayDate(row.signOnDate),
                },
                {
                  key: 'signOffDate',
                  header: 'Sign off',
                  sortable: false,
                  render: (_value, row) => displayDate(row.signOffDate),
                },
              ]}
              keyExtractor={(row) => row.key}
              emptyMessage="No services recorded."
              actions={(row) => renderRowActions(row.section, row.source)}
              density="compact"
              tableWrapperClassName="w-full"
              onRowClick={(row) => activateRecordRow(row.section, row.source)}
            />
          </TabsContent>

          {showPreviousServices && (
            <TabsContent value="previous" className="!mt-0">
              {renderRecordsTable({
                section: 'prevServices',
                columns: ['Status', 'Vessel', 'Rank', 'Sign on', 'Sign off', 'Manager'],
                rows: form.prevServices,
                emptyMessage: 'No previous service recorded.',
                addLabel: 'Add previous service',
                renderCells: (row) => {
                  const r = row as PrevServiceFormRow;
                  const incomplete = prevServiceIncomplete(r);
                  return [
                    incomplete ? (
                      <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:text-amber-300">
                        Incomplete
                      </span>
                    ) : (
                      serviceStatusBadge('OFB')
                    ),
                    displayText(r.vesselName),
                    optionLabel(rankOpts, r.rankId),
                    displayDate(r.signOnDate || r.startDate),
                    displayDate(r.signOffDate || r.endDate),
                    displayText(r.managerName),
                  ];
                },
              })}
            </TabsContent>
          )}

          {showCompanyServices && (
            <TabsContent value="company" className="!mt-0">
              {renderRecordsTable({
                section: 'transactions',
                columns: ['Status', 'Vessel', 'Rank', 'Sign on', 'Sign off', 'Total wage'],
                rows: form.transactions,
                emptyMessage: 'No company services recorded.',
                addLabel: 'Add company service',
                renderCells: (row) => {
                  const r = row as TransactionFormRow;
                  const vesselLabel = r.vesselId
                    ? optionLabel(vesselOpts, r.vesselId)
                    : displayText(r.vesselName);
                  return [
                    serviceStatusBadge(r.status),
                    vesselLabel,
                    optionLabel(rankOpts, r.rankId),
                    displayDate(r.signOnDate),
                    displayDate(r.signOffDate),
                    displayText(r.totalWage),
                  ];
                },
              })}
            </TabsContent>
          )}
        </Tabs>
      </TabsContent>

      <TabsContent value="medicals" className="mt-0">
        {renderRecordsTable({
          section: 'medicals',
          columns: ['Kind', 'Exam date', 'Next due', 'Result', 'Clinic'],
          rows: form.medicals,
          emptyMessage: 'No medical records.',
          addLabel: 'Add medical',
          renderCells: (row) => {
            const r = row as MedicalFormRow;
            return [
              optionLabel(medicalKindOpts, r.kind),
              displayDate(r.examDate),
              displayDate(r.nextDueDate),
              optionLabel(medicalResultOpts, r.result),
              displayText(r.clinicName),
            ];
          },
        })}
      </TabsContent>

      <Modal isOpen={editor.open} onClose={closeEditor} size={modalSize}>
        <ModalHeader>
          <div className="min-w-0 pr-8">
            <ModalTitle>{editorTitle()}</ModalTitle>
            {editorSubtitle() ? (
              <p className={cn('mt-1 text-sm', colors.textMuted)}>{editorSubtitle()}</p>
            ) : null}
          </div>
        </ModalHeader>
        <ModalContent className={colors.headerBg}>
          {renderEditorBody()}
        </ModalContent>
        <ModalFooter className={cn('backdrop-blur-sm', colors.surface)}>
          {editorFieldError ? (
            <p className="mr-auto max-w-md text-xs text-red-600">{editorFieldError}</p>
          ) : null}
          <Button type="button" variant="outline" onClick={closeEditor} className="rounded-xl min-w-[5.5rem]">
            {editor.open && (readOnly || editor.mode === 'view') ? 'Close' : 'Cancel'}
          </Button>
          {editor.open && !readOnly && editor.mode !== 'view' && (
            <Button type="button" onClick={saveEditor} className="rounded-xl min-w-[5.5rem]">
              Save
            </Button>
          )}
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDeleteRow}
        title={deleteConfirm ? `Delete ${SECTION_LABELS[deleteConfirm.section]}?` : 'Delete record?'}
        message="This removes the row from the form. Save the crew file to keep the change."
        details={deleteConfirm?.summary ? { Record: deleteConfirm.summary } : null}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        zIndex={130}
      />
    </>
  );
};

export default CrewRecordsSections;
